import React from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@provider-portal/design-common/kalep/components/Box/Box';
import { Drawer } from '@provider-portal/design-common/kalep/components/Drawer/Drawer';

import { NavBarLogo } from './NavBarLogo';
import { NavBarMenu, NavBarMenuItem } from './NavBarMenu';
import { NavBarVenueSelector, NavBarVenueSelectorProps } from './NavBarVenueSelector';

export interface NavBarDrawerProps {
  isMobileOpen: boolean;
  drawerWidth: number;
  menuItems: NavBarMenuItem[];
  venueSelector: NavBarVenueSelectorProps;
  onDrawerToggle: () => void;
  onMenuItemClick?: (menuItem: NavBarMenuItem) => void;
  onSelectorOpen?: () => void;
  onSearchPerformed?: (searchQuery: string, resultsCount: number, searchCount: number) => void;
}

export function NavBarDrawer({
  isMobileOpen,
  drawerWidth,
  menuItems,
  venueSelector,
  onDrawerToggle,
  onMenuItemClick,
  onSelectorOpen,
  onSearchPerformed,
}: NavBarDrawerProps) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = React.useCallback(() => {
    onDrawerToggle();
  }, [onDrawerToggle]);

  const drawerWidthWithPadding = isMobileView ? drawerWidth : drawerWidth + 18;

  return (
    <>
      <Drawer
        variant={isMobileView ? 'temporary' : 'permanent'}
        anchor="left"
        borderRadius="0px"
        open={isMobileView ? isMobileOpen : undefined}
        onClose={isMobileView ? handleDrawerToggle : undefined}
        ModalProps={
          isMobileView
            ? {
                keepMounted: true, // Better open performance on mobile.
              }
            : undefined
        }
        sx={{
          display: { xs: 'block', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidthWithPadding,
            padding: '20px 8px',
            borderRight: `1px solid ${theme.palette.colors.border.separator}`,
            boxShadow: 'none !important',
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap="8px">
          <NavBarLogo />
          {!isMobileView && (
            <NavBarVenueSelector
              parentName={venueSelector.parentName}
              storeName={venueSelector.storeName}
              selectedStoreId={venueSelector.selectedStoreId}
              stores={venueSelector.stores}
              storeAvailabilities={venueSelector.storeAvailabilities}
              storeAvailabilitiesLoading={venueSelector.storeAvailabilitiesLoading}
              onProviderSelect={venueSelector.onProviderSelect}
              showAddVenueButton={venueSelector.showAddVenueButton}
              onAddVenue={venueSelector.onAddVenue}
              onSelectorOpen={onSelectorOpen}
              onSearchPerformed={onSearchPerformed}
            />
          )}
          <NavBarMenu menuItems={menuItems} onMenuItemClick={onMenuItemClick} />
        </Box>
      </Drawer>
    </>
  );
}
